/**
 * BaselineProgress — Shows V1 baseline calibration status.
 * Displays each of the 5 foundational lifts with freshness state.
 * Used on profile, pre-duel, and pre-leaderboard screens.
 */

import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { CheckCircle, Circle, AlertTriangle, Clock } from 'lucide-react-native';
import type { BaselineStatus, FreshnessStatus } from '@/lib/baselineGate';

interface Props {
  status: BaselineStatus;
  compact?: boolean;
}

const STATUS_CONFIG: Record<FreshnessStatus, { color: string; label: string }> = {
  fresh: { color: '#22c55e', label: 'Fresh' },
  aging: { color: '#eab308', label: 'Aging' },
  stale: { color: '#f97316', label: 'Stale' },
  expired: { color: '#ef4444', label: 'Expired' },
  missing: { color: '#6b7280', label: 'Not logged' },
};

export default function BaselineProgress({ status, compact }: Props) {
  const { theme } = useTheme();

  const renderIcon = (freshnessStatus: FreshnessStatus) => {
    const config = STATUS_CONFIG[freshnessStatus];
    switch (freshnessStatus) {
      case 'fresh':
        return <CheckCircle size={compact ? 14 : 18} color={config.color} />;
      case 'aging':
        return <Clock size={compact ? 14 : 18} color={config.color} />;
      case 'stale':
        return <AlertTriangle size={compact ? 14 : 18} color={config.color} />;
      case 'expired':
        return <AlertTriangle size={compact ? 14 : 18} color={config.color} />;
      case 'missing':
      default:
        return <Circle size={compact ? 14 : 18} color={config.color} />;
    }
  };

  return (
    <View style={[styles.container, compact && styles.containerCompact]}>
      {!compact && (
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            Core 5 Calibration
          </Text>
          <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
            {status.isComplete
              ? 'All 5 lifts logged — Core 5 complete'
              : `${status.liftsLoggedEver}/5 lifts logged`}
          </Text>
        </View>
      )}

      <View style={styles.progressBar}>
        {status.lifts.map((lift, i) => {
          const config = STATUS_CONFIG[lift.status];
          return (
            <View
              key={lift.lift}
              style={[
                styles.progressSegment,
                {
                  backgroundColor: lift.status === 'missing' ? theme.colors.border : config.color,
                  opacity: lift.status === 'missing' ? 0.3 : 1,
                },
                i === 0 && styles.progressFirst,
                i === status.lifts.length - 1 && styles.progressLast,
              ]}
            />
          );
        })}
      </View>

      {!compact && (
        <View style={styles.liftList}>
          {status.lifts.map((lift) => {
            const config = STATUS_CONFIG[lift.status];
            return (
              <View key={lift.lift} style={styles.liftRow}>
                {renderIcon(lift.status)}
                <Text style={[styles.liftName, { color: theme.colors.text }]}>
                  {lift.label}
                </Text>
                <Text style={[styles.liftStatus, { color: config.color }]}>
                  {config.label}
                  {lift.daysSinceLog !== null && lift.status !== 'missing'
                    ? ` (${lift.daysSinceLog}d)`
                    : ''}
                </Text>
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  containerCompact: {
    gap: 6,
  },
  header: {
    gap: 4,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: '500',
  },
  progressBar: {
    flexDirection: 'row',
    height: 6,
    gap: 3,
  },
  progressSegment: {
    flex: 1,
    borderRadius: 3,
  },
  progressFirst: {
    borderTopLeftRadius: 3,
    borderBottomLeftRadius: 3,
  },
  progressLast: {
    borderTopRightRadius: 3,
    borderBottomRightRadius: 3,
  },
  liftList: {
    gap: 8,
  },
  liftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  liftName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  liftStatus: {
    fontSize: 12,
    fontWeight: '600',
  },
});
